import { create } from 'zustand';
import type { OrderProblemDetailsResponse } from '@/core/generated/api';
import { RequestStatus } from '@/shared/model/requestStatus';
import { getErrorMessage } from '@/shared/lib/errors';
import { ORDER_PROBLEMS_CHANGED_EVENT } from '@/core/hooks/useOrderProblemCount';
import { orderProblemService } from '../api/orderProblemService';
import type { OrderProblemData, OrderProblemActions } from './types';

const initialState: OrderProblemData = {
    items: [],
    allCount: 0,
    activeCount: 0,
    current: null,
    currentId: null,
    listStatus: RequestStatus.Idle,
    detailsStatus: RequestStatus.Idle,
    mutationStatus: RequestStatus.Idle,
    listError: null,
    detailsError: null,
    mutationError: null,
};

let listRevision = 0;
let detailsRevision = 0;
let mutationRevision = 0;

/** Хранит список и диагностику проблемных заказов */
export const useOrderProblems = create<OrderProblemData & OrderProblemActions>((set, get) => {
    /** Применяет действие к проблеме заказа */
    const mutate = async (
        id: string,
        action: (problemId: string) => Promise<OrderProblemDetailsResponse>,
    ): Promise<boolean> => {
        if (get().mutationStatus === RequestStatus.Loading) return false;

        const revision = ++mutationRevision;

        set({
            mutationStatus: RequestStatus.Loading,
            mutationError: null
        });

        try {
            const response = await action(id);

            if (revision !== mutationRevision) return false;

            if (get().currentId === id) set({ current: response });

            set({ mutationStatus: RequestStatus.Success });

            if (typeof window !== 'undefined') window.dispatchEvent(new Event(ORDER_PROBLEMS_CHANGED_EVENT));

            return true;
        } catch (error: unknown) {
            if (revision !== mutationRevision) return false;

            set({
                mutationStatus: RequestStatus.Error,
                mutationError: getErrorMessage(error, 'Не удалось обработать проблему заказа'),
            });

            return false;
        }
    };

    return {
        ...initialState,

        fetchList: async (query) => {
            const revision = ++listRevision;

            set({
                listStatus: RequestStatus.Loading,
                listError: null
            });

            try {
                const response = await orderProblemService.getList(query);

                if (revision !== listRevision) return;

                set({
                    items: response.problems ?? [],
                    allCount: response.allCount ?? 0,
                    activeCount: response.activeCount ?? 0,
                    listStatus: RequestStatus.Success,
                });
            } catch (error: unknown) {
                if (revision !== listRevision) return;

                set({
                    listStatus: RequestStatus.Error,
                    listError: getErrorMessage(error, 'Не удалось загрузить проблемные заказы'),
                });
            }
        },

        fetchDetails: async (id) => {
            const revision = ++detailsRevision;

            set({
                currentId: id,
                current: null,
                detailsStatus: RequestStatus.Loading,
                detailsError: null,
                mutationError: null,
            });

            mutationRevision++;
            set({ mutationStatus: RequestStatus.Idle });

            try {
                const current = await orderProblemService.getDetails(id);

                if (revision !== detailsRevision) return;

                set({
                    current,
                    detailsStatus: RequestStatus.Success
                });
            } catch (error: unknown) {
                if (revision !== detailsRevision) return;

                set({
                    detailsStatus: RequestStatus.Error,
                    detailsError: getErrorMessage(error, 'Не удалось загрузить диагностику заказа'),
                });
            }
        },

        retry: (id) => mutate(id, orderProblemService.retry),

        resolve: (id) => mutate(id, orderProblemService.resolve),

        reset: () => {
            listRevision++;
            detailsRevision++;
            mutationRevision++;
            set(initialState);
        },
    };
});
